import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { formatMoney } from "@bookboost/shared/format";

interface Stage {
  id: string;
  label: string;
  description: string;
  count: number;
  valueEur: number;
  dropCount?: number;
  dropValueEur?: number;
  overallPct?: number;
  stagePct?: number;
}
interface FunnelResponse {
  year: number;
  baseCurrency: string;
  fxWarnings: string[];
  stages: Stage[];
  transitions: {
    wonToInvoiceDays: number | null;
    invoiceToPaidDays: number | null;
    paidToCommissionDays: number | null;
    commissionToPayoutDays: number | null;
  };
}

const funnelSearch = z.object({ year: z.coerce.number().optional() });

export const Route = createFileRoute("/admin/funnel")({
  validateSearch: funnelSearch,
  component: FunnelPage,
});

function FunnelPage() {
  const { year } = Route.useSearch();
  const navigate = Route.useNavigate();
  const thisYear = new Date().getUTCFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2];

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "funnel", { year }],
    queryFn: () => api.get<FunnelResponse>("/api/admin/funnel", { searchParams: { year } }),
  });

  if (isLoading) return <p className="lede">Computing funnel…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Funnel unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  const stageOne = data.stages[0];
  const maxValue = Math.max(...data.stages.map((s) => s.valueEur), 1);

  return (
    <>
      <h1>Conversion funnel · {data.year}</h1>

      <form className="inline filters" style={{ gap: 12, marginBottom: 16 }} onSubmit={(e) => e.preventDefault()}>
        <label className="muted">Year</label>
        <select value={String(data.year)} onChange={(e) => navigate({ search: { year: Number(e.target.value) } })}>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>Amounts in {data.baseCurrency}.</p>
      </form>

      {data.fxWarnings.length > 0 && (
        <Panel variant="warn">
          <strong>⚠ FX rates missing for:</strong> {data.fxWarnings.join(", ")} — values are understated.
        </Panel>
      )}

      <div className="grid cols-4">
        <Metric
          label="Won → invoice"
          value={data.transitions.wonToInvoiceDays != null ? `${data.transitions.wonToInvoiceDays.toFixed(1)}d` : "—"}
          sub="mean days"
          hint="Average time from HubSpot deal closed-won to the first Stripe invoice being created."
        />
        <Metric
          label="Invoice → paid"
          value={data.transitions.invoiceToPaidDays != null ? `${data.transitions.invoiceToPaidDays.toFixed(1)}d` : "—"}
          sub="mean days"
          hint="Average time from first invoice issued to first invoice paid."
        />
        <Metric
          label="Paid → commission"
          value={data.transitions.paidToCommissionDays != null ? `${data.transitions.paidToCommissionDays.toFixed(1)}d` : "—"}
          sub="mean days"
          hint="Average time from paid invoice to commission row being written. Usually a few seconds — webhook latency."
        />
        <Metric
          label="Commission → payout"
          value={data.transitions.commissionToPayoutDays != null ? `${data.transitions.commissionToPayoutDays.toFixed(1)}d` : "—"}
          sub="mean days"
          hint="Average time from commission earned to finance marking it paid."
        />
      </div>

      <Panel padding="tight">
        <div style={{ padding: "16px 20px" }}>
          {data.stages.map((stage, idx) => {
            const widthPct = stage.count > 0 ? Math.max(8, (stage.valueEur / maxValue) * 100) : 8;
            return (
              <div key={stage.id} style={{ marginBottom: idx === data.stages.length - 1 ? 0 : 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <div>
                    <strong>{stage.label}</strong>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>{stage.description}</span>
                  </div>
                  <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
                    {stage.count} deals · {formatMoney(stage.valueEur, data.baseCurrency)}
                  </div>
                </div>
                <div style={{ position: "relative", height: 32, background: "var(--gray100)", borderRadius: 4, overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${widthPct}%`,
                      height: "100%",
                      background:
                        idx === 0
                          ? "var(--primary600)"
                          : idx === data.stages.length - 1
                            ? "var(--accent-2)"
                            : `color-mix(in srgb, var(--primary600) ${100 - idx * 18}%, var(--primary200))`,
                      transition: "width 240ms ease",
                    }}
                  />
                  {stage.overallPct != null && stageOne && stageOne.count > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: 8,
                        transform: "translateY(-50%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 12,
                        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      }}
                    >
                      {stage.overallPct.toFixed(0)}% of closed-won
                    </span>
                  )}
                </div>
                {idx > 0 && stage.dropCount != null && stage.dropCount > 0 && (
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                    ↓ {stage.dropCount} dropped (
                    {formatMoney(stage.dropValueEur ?? 0, data.baseCurrency)}) · stage conversion{" "}
                    {stage.stagePct?.toFixed(0)}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Panel>
    </>
  );
}
